import { Container } from "pixi.js";
import type { Sprite } from "pixi.js";
import type { ScreenShape } from "../responsive/ScreenShape";
import type { ViewportStore } from "../responsive/ViewportStore";

export type CoverBackgroundVariantId = ScreenShape | "shared";

export interface CoverBackgroundViewportRule {
  /** Rule applies only when viewport.width <= this value. 0 = no width constraint. */
  maxViewportWidth?: number;
  /** Rule applies only when viewport.height <= this value. 0 = no height constraint. */
  maxViewportHeight?: number;
  scaleMultiplier?: number;
  /** Position offset in design units, y-down. */
  offsetX?: number;
  offsetY?: number;
}

/** One cover-fit source: a sprite or any container with a known source size (a Spine background). */
export interface CoverBackgroundVariant {
  /** The display object that gets cover-scaled. */
  target: Container;
  /** Source size the cover ratio is computed against (texture size for sprites). */
  sourceWidth: number;
  sourceHeight: number;
  scaleMultiplier?: number;
  /** Position offset in design units, y-down. */
  offsetX?: number;
  offsetY?: number;
  viewportRules?: CoverBackgroundViewportRule[];
  /** Optional hook when this variant becomes active (e.g. start a Spine loop). */
  onActivate?: () => void;
}

export function spriteVariant(sprite: Sprite, extra: Omit<CoverBackgroundVariant, "target" | "sourceWidth" | "sourceHeight"> = {}): CoverBackgroundVariant {
  sprite.anchor.set(0.5);
  return { target: sprite, sourceWidth: sprite.texture.width, sourceHeight: sprite.texture.height, ...extra };
}

/**
 * A cover-fit source with an AUTHORED size — a Spine background inside its prefab box (the
 * `spineBackground` prefab: 1442×1103 around the skeleton), or any container. `target` is scaled so
 * that box covers the viewport; its origin must be the box centre.
 */
export function sizedVariant(
  target: Container,
  width: number,
  height: number,
  extra: Omit<CoverBackgroundVariant, "target" | "sourceWidth" | "sourceHeight"> = {},
): CoverBackgroundVariant {
  return { target, sourceWidth: width, sourceHeight: height, ...extra };
}

/**
 * Cover-fit background (CSS `object-fit: cover` semantics) with one source per
 * device variant, or a single `shared` source. Follows the
 * `ResponsiveCoverBackground`: scale = max(viewport/source) × overscan ×
 * variant multiplier; viewport rules override multiplier/offset by size.
 * The container's origin is the viewport CENTRE (add it to a centred layer).
 */
export class CoverBackground extends Container {
  private readonly variants: Partial<Record<CoverBackgroundVariantId, CoverBackgroundVariant>>;
  private readonly overscanMultiplier: number;
  private current: CoverBackgroundVariantId;
  private viewportWidth = 0;
  private viewportHeight = 0;
  private unsubscribe: (() => void) | null = null;

  public constructor(input: {
    variants: Partial<Record<CoverBackgroundVariantId, CoverBackgroundVariant>>;
    /** Extra inflation to mask edge seams; clamped to >= 1. Default 1.01. */
    overscanMultiplier?: number;
  }) {
    super();
    this.variants = input.variants;
    this.overscanMultiplier = Math.max(1, input.overscanMultiplier ?? 1.01);
    this.current = input.variants.shared ? "shared" : "mobile";
    for (const variant of Object.values(this.variants)) {
      if (variant) {
        variant.target.visible = false;
        this.addChild(variant.target);
      }
    }
  }

  /** Follows the viewport store: the shape picks the variant, the design size is the area to cover. */
  public bind(viewport: ViewportStore): void {
    this.unsubscribe?.();
    this.unsubscribe = viewport.subscribe((v) => {
      this.current = this.variants.shared ? "shared" : v.shape;
      this.viewportWidth = v.design.width;
      this.viewportHeight = v.design.height;
      this.applyCoverScale();
    });
  }

  public dispose(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  public applyCoverScale(): void {
    const activeId = this.current;
    let activated: CoverBackgroundVariant | null = null;
    for (const [id, variant] of Object.entries(this.variants) as Array<[CoverBackgroundVariantId, CoverBackgroundVariant]>) {
      const on = id === activeId;
      if (variant.target.visible !== on) {
        variant.target.visible = on;
        if (on) activated = variant;
      }
    }
    const variant = this.variants[activeId];
    if (!variant) return;
    if (activated) activated.onActivate?.();

    const { sourceWidth, sourceHeight } = variant;
    if (sourceWidth <= 0 || sourceHeight <= 0 || this.viewportWidth <= 0 || this.viewportHeight <= 0) return;

    const applied = this.resolveAppliedTransform(variant);
    const coverScale = Math.max(this.viewportWidth / sourceWidth, this.viewportHeight / sourceHeight) * this.overscanMultiplier;
    const finalScale = coverScale * applied.scaleMultiplier;
    variant.target.position.set(applied.offsetX, applied.offsetY);
    variant.target.scale.set(finalScale, finalScale);
  }

  private resolveAppliedTransform(variant: CoverBackgroundVariant): { scaleMultiplier: number; offsetX: number; offsetY: number } {
    for (const rule of variant.viewportRules ?? []) {
      const maxW = rule.maxViewportWidth ?? 0;
      const maxH = rule.maxViewportHeight ?? 0;
      if (maxW > 0 && this.viewportWidth > maxW) continue;
      if (maxH > 0 && this.viewportHeight > maxH) continue;
      return { scaleMultiplier: rule.scaleMultiplier ?? 1, offsetX: rule.offsetX ?? 0, offsetY: rule.offsetY ?? 0 };
    }
    return { scaleMultiplier: variant.scaleMultiplier ?? 1, offsetX: variant.offsetX ?? 0, offsetY: variant.offsetY ?? 0 };
  }
}
